import React from 'react';
import { CircuitRenderer, CircuitElement } from './CircuitRenderer';

interface PowerDiagramProps {
    voltage: number;
    resistance: number;
    current: number;
    power: number;
    solveFor: 'current' | 'voltage' | 'resistance' | 'power';
}

export const PowerDiagram: React.FC<PowerDiagramProps> = ({
    voltage,
    resistance,
    current,
    power,
    solveFor
}) => {
    // Layout constants
    const width = 400;
    const height = 300;
    const padding = 50;

    const leftX = padding + 40;
    const rightX = width - padding - 40;
    const topY = padding + 20;
    const bottomY = height - padding - 20;

    const highlightColor = "#2563eb";

    const vLabel = solveFor === 'voltage' ? 'V = ?' : undefined;
    const rLabel = solveFor === 'resistance' ? 'R = ?' : `${resistance}Ω`;
    const iLabel = solveFor === 'current' ? 'I = ?' : `I = ${current}A`;
    const pLabel = solveFor === 'power' ? 'P = ?' : `P = ${power}W`;

    const elements: CircuitElement[] = [
        // Top Wire
        {
            type: 'wire',
            from: { x: leftX, y: topY },
            to: { x: rightX, y: topY },
            labelSide: 'left'
        },

        // Right Resistor
        {
            type: 'resistor',
            from: { x: rightX, y: topY },
            to: { x: rightX, y: bottomY },
            value: resistance,
            label: rLabel,
            labelColor: solveFor === 'resistance' ? highlightColor : undefined,
            labelSide: 'left'
        },

        // Bottom Wire / Current Arrow
        {
            type: 'currentArrow',
            from: { x: rightX, y: bottomY },
            to: { x: leftX, y: bottomY },
            label: iLabel,
            labelColor: solveFor === 'current' ? highlightColor : undefined,
            labelOffset: 30,
            labelSide: 'left'
        },

        // Left Voltage Source
        {
            type: 'voltageSource',
            from: { x: leftX, y: bottomY },
            to: { x: leftX, y: topY },
            value: voltage,
            label: vLabel,
            labelColor: solveFor === 'voltage' ? highlightColor : undefined,
            labelSide: 'left'
        }
    ];

    // We need to show Power label in center.
    // We can use an invisible junction with label for arbitrary text placement.
    const junctions = [
        {
            x: width / 2,
            y: height / 2,
            label: pLabel,
            color: 'transparent', // Invisible point
            labelPosition: 'bottom' as const, // Or center? Junction label logic offsets it.
            // Let's rely on standard offset.
            labelOffset: 0
        }
    ];

    return (
        <CircuitRenderer width={width} height={height} elements={elements} junctions={junctions} />
    );
};
